import Tools from "@/components/Builder/Tools";
import StudentDashboardLayout from "@/components/Student/DashboardLayout";
import Selector from "@/components/Student/Selector";
import { useAuthContext } from "@/context/auth/AuthContext";
import LoadAndDisplay from "@/context/LoadAndDisplay";
import axios from "axios";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import CourseWidgets from "@/components/Instructor/Dashboard-Section/widgets/CourseWidget";
import DataDisplay from "@/components/Common/DataFetcher";
import { connectToDatabase } from "@/components/Server/Database/MongoDB";
import researchIcon from "../../../public/images/icons/research-cv.webp";

const SubjectSystemSelector = ({ info }) => {
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [data, setData] = useState({});
  const [history, setHistory] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const router = useRouter();
  const { user } = useAuthContext();
  const updateTotalQuestions = (questionDifference) => {
    setTotalQuestions((prevTotal) => prevTotal + questionDifference);
  };

  const handleSubjectsChange = (subjects) => {
    setSelectedSubjects(subjects);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post(`/api/question/test`, {
        subjects: selectedSubjects,
        displayName: state.displayName,
        userId: user._id,
        questionCount: Number(state.questionCount) || 40,
      });

      if (response.status === 200) {
        router.push(
          `/student/question/test/${response.data.status.insertedId}`
        ); // Adjust the path as needed
      }
      setLoading(false);
    } catch (err) {
      console.error("Error creating test:", err);
      setLoading(false);
    } finally {
    }
  };

  const getHistory = async (
    currentPage,
    limit,
    search,
    searchBy,
    setPages,
    setError,
    setLoading
  ) => {
    if (user)
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/question/test/history?userId=${user._id}&page=${currentPage}&limit=${limit}&search=${search}&searchBy=${searchBy}`
        );

        if (response.status === 200) {
          setHistory(response.data.data);
          setPages(response.data.pages);
          setError(null);
        } else {
          setError(
            response.data.message || "An error occurred during data fetching."
          );
        }
      } catch (err) {
        setError("Fetching data failed. Please try again later.");
      } finally {
        setLoading(false);
      }
  };

  const getAssignments = async (
    currentPage,
    limit,
    search,
    searchBy,
    setPages,
    setError,
    setLoading
  ) => {
    if (user)
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/data?collection=system-tests&page=${currentPage}&limit=${limit}&search=${
            user.batch.module
          }${search ? `,${search}` : ""}&searchBy=module${
            search ? `,${searchBy}` : ""
          }&operation=module.$in`
        );

        if (response.status === 200) {
          setAssignments(response.data.data);
          setPages(response.data.pages);
          setError(null);
        } else {
          setError(
            response.data.message || "An error occurred during data fetching."
          );
        }
      } catch (err) {
        setError("Fetching data failed. Please try again later.");
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`/api/question/subjects`);
        setData(response.data);
      } catch (err) {
        console.error("Error fetching subjects:", err);
      }
    };

    fetchData();
  }, []);
  const tabs = [
    {
      id: "test",
      selected: true,
      displayName: "Start Test",
      component: (
        <div className="content row">
          <Tools
            state={state}
            setState={setState}
            inputs={[
              {
                element: "text",
                name: "displayName",
                size: "sm",
                label: "Test Name",
                width: "col-md-6",
                required: true,
                placeholder: "Enter a Test Title",
              },
              {
                element: "select",
                items: "10, 20, 30, 40",
                name: "questionCount",
                label: "Number of Questions",
                width: "col-md-6",
                required: false,
                placeholder: "Enter the Number of Questions",
              },
            ]}
          />
          {data.subjects && (
            <Selector
              label="Select All Subjects"
              items={data.subjects}
              updateTotalQuestions={updateTotalQuestions}
              onChange={handleSubjectsChange}
            />
          )}

          <form
            onSubmit={handleSubmit}
            className="gap-4 mt-5 d-flex flex-column"
          >
            <LoadAndDisplay isLoading={loading}>
              <button
                disabled={loading && totalQuestions > 0}
                type="submit"
                className="rbt-btn btn-sm"
              >
                Initiate Test with{" "}
                {totalQuestions > 0 ? state.questionCount || 40 : 0} questions
              </button>
            </LoadAndDisplay>
          </form>
        </div>
      ),
    },
    {
      id: "assignments",
      selected: false,
      displayName: "Test Assignments",
      component: (
        <DataDisplay
          user={user}
          fetchData={getAssignments}
          setData={setAssignments}
          empty={assignments.length === 0}
          config={{
            empty: {
              image: {
                src: researchIcon,
                width: 100,
                height: 100,
              },
              title: "Nothing to Show",
              subtitle: "Currently there are no assignments available",
            },
          }}
        >
          {assignments?.map((slide, index) => (
            <div className="col-md-4 col-12" key={`course-enrolled-${index}`}>
              <CourseWidgets
                data={{
                  ...slide,
                  displayName: slide.displayName,
                  shortDescription: new Date(slide.createdAt).toDateString(),
                }}
                image={true}
                href={`/student/question/test/${slide._id}?type=system`}
                courseStyle="two"
                isProgress={false}
                isCompleted={false}
                isEdit={false}
                showDescription={true}
                showAuthor={false}
                button="Give Test"
              />
            </div>
          ))}
        </DataDisplay>
      ),
    },
    {
      id: "history",
      displayName: "Test History",
      selected: false,
      component: (
        <DataDisplay
          empty={history.length === 0}
          user={user}
          fetchData={getHistory}
          setData={setHistory}
          entryLimit={6}
          config={{
            empty: {
              image: {
                src: researchIcon,
                width: 100,
                height: 100,
              },
              title: "Nothing to Show",
              subtitle: "You have not started any tests",
            },
          }}
        >
          {history?.map((slide, index) => (
            <div className="col-md-4 col-12" key={`course-enrolled-${index}`}>
              <CourseWidgets
                data={{
                  ...slide,
                  shortDescription: new Date(slide.createdAt).toDateString(),
                }}
                image={true}
                href={`/student/question/test/summary/${slide._id}`}
                courseStyle="two"
                progressTitle="Percentage"
                isProgress={true}
                isCompleted={false}
                isEdit={false}
                showDescription={true}
                showAuthor={false}
                button="View Results"
              />
            </div>
          ))}
        </DataDisplay>
      ),
    },
  ];

  return (
    <StudentDashboardLayout brand={JSON.parse(info)} sidebar={true}>
      <div className="rbt-dashboard-content bg-color-white rbt-shadow-box">
        <div className="advance-tab-button mb--30">
          <ul
            className="nav nav-tabs tab-button-style-2 justify-content-start"
            id="myTab-4"
            role="tablist"
          >
            {tabs.map((tab, index) => {
              return (
                <li key={tab.id} role="presentation">
                  <Link
                    href="#"
                    className={`tab-button ${tab.selected ? "active" : ""}`}
                    id={tab.id}
                    data-bs-toggle="tab"
                    data-bs-target={`#${tab.id}-${index}`}
                    role="tab"
                    aria-controls={`${tab.id}-${index}`}
                    aria-selected={tab.selected}
                  >
                    <span className="title">{tab.displayName}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="tab-content">
          {tabs.map((tab, index) => {
            return (
              <div
                key={`${tab.id}-${index}`}
                className={`tab-pane fade ${tab.selected ? "active show" : ""}`}
                id={`${tab.id}-${index}`}
                role="tabpanel"
                aria-labelledby={tab.id}
              >
                {tab.component}
              </div>
            );
          })}
        </div>
      </div>
    </StudentDashboardLayout>
  );
};

export const getServerSideProps = async (context) => {
  const domainName = context.req ? context.req.headers.host : null;

  const { db } = await connectToDatabase();

  const info = await db.collection("partners").findOne({
    key: process.env.DEFAULT || domainName,
  });

  if (!info) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      info: JSON.stringify(info),
    },
  };
};
export default SubjectSystemSelector;
